import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, Loader2, Filter } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { brosAPI, reportsAPI } from '../../lib/api';
import { formatCurrency, cn, deriveInvoiceStatus } from '../../lib/utils';
import { convertAmount } from '../../lib/currencyConvert';
import { invoiceParty, asCancellationRow } from '../../lib/brosRecordMoney';
import { bookingsByDueMonth, bookingByClosingMonth, invoiceByDueMonth } from '../../lib/brosListRows';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { InvoicePdfButton } from '../InvoicePdfButton';
import { invoicePaidAmount } from '../../lib/invoicePaid';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Inner component: props destructured in body (babel-metadata-plugin workaround)
function PaymentStatusBadge(props) {
  const { status } = props;
  const styles = {
    paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0',
    unpaid: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0',
    overdue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0',
    draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-0',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0',
    void: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400 border-0',
  };
  const label = (status || 'unknown').replace(/_/g, ' ');
  return (
    <Badge className={cn('text-[10px] px-1.5 py-0 ml-2', styles[status] || '')}>
      {label.charAt(0).toUpperCase() + label.slice(1)}
    </Badge>
  );
}

// Shared list component for BR OS bookings and invoices.
// Follows the CountryBookingsContent pattern: monthly groups, expandable detail rows.
// Props destructured in body (babel-metadata-plugin workaround).
//
// basis (Fan, 2026-10-28: "Bookings and Invoices, please group by due date.
// And the bookings by closing date should be under the Sales tab"):
//   'due'     (default) P&L / Cashflow lists. Bookings = each booking's share
//             of the month's revenue, the rows behind the P&L chart;
//             Invoices = by due date, ex VAT. Month totals = the chart.
//   'closing' Sales > Bookings: the whole booking in its closing month
//             (closedOn from the Sales tab, else creation date).
export function BROSRecordsList(props) {
  const { type, showPaymentStatus, displayCurrency, fxRates, basis = 'due', closedOn } = props;
  const dueBasis = basis !== 'closing';
  const dc = displayCurrency || 'GBP';
  const cv = (amount, currency) => convertAmount(amount || 0, currency || 'GBP', dc, fxRates) || 0;

  const [records, setRecords] = useState([]);
  // Dashboard drill (due basis only): months[].bookings + revenue_invoice_keys.
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  // Filters
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  // Invoices tab: client and supplier invoices are never summed together
  // (one is money in, the other money out). Default to client invoices.
  const [invoiceType, setInvoiceType] = useState('client');

  useEffect(() => {
    let cancelled = false;
    const doFetch = async () => {
      setLoading(true);
      try {
        const [res, drillRes] = await Promise.all([
          type === 'bookings'
            ? brosAPI.getBookings({ limit: 5000 })
            : brosAPI.getInvoices({ limit: 5000 }),
          dueBasis ? reportsAPI.brosDashboardDrill() : Promise.resolve(null),
        ]);
        if (!cancelled) {
          setRecords(type === 'bookings' ? (res.data.bookings || []).map(asCancellationRow) : (res.data.invoices || []));
          setDrill(drillRes ? drillRes.data : null);
        }
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [type, dueBasis]);

  // Normalised rows (lib/brosListRows): every row carries _mk (month),
  // _cur and _excluded, plus its values, whatever the basis.
  const rows = useMemo(() => {
    if (type === 'bookings') {
      return dueBasis
        ? bookingsByDueMonth(drill?.months, records)
        : records.map(b => bookingByClosingMonth(b, fxRates, closedOn));
    }
    const keys = drill?.revenue_invoice_keys ? new Set(drill.revenue_invoice_keys) : null;
    return records.map(inv => invoiceByDueMonth(inv, invoicePaidAmount(inv), keys));
  }, [records, drill, type, dueBasis, fxRates, closedOn]);

  // Filter options
  const companyOptions = useMemo(() => {
    const names = new Set();
    rows.forEach(r => {
      if (type === 'invoices' && (r.type || 'client') !== invoiceType) return;
      const name = r.company || r.client_name || r.supplier_name || '';
      if (name) names.add(name);
    });
    return [...names].sort();
  }, [rows, type, invoiceType]);

  // Invoices: unpaid and past due shows as "overdue" (BR OS stores no
  // overdue status; it is derived). Same helper as the Revenue/Costs pages.
  const rowStatus = (r) => (type === 'invoices' ? deriveInvoiceStatus(r) : r.status);

  const statusOptions = useMemo(() => {
    const vals = new Set();
    rows.forEach(r => {
      if (type === 'invoices' && (r.type || 'client') !== invoiceType) return;
      const st = rowStatus(r);
      if (st) vals.add(st);
    });
    return [...vals].sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, type, invoiceType]);

  // Apply filters
  const filtered = useMemo(() => {
    let result = rows;
    if (type === 'invoices') {
      result = result.filter(r => (r.type || 'client') === invoiceType);
    }
    if (selectedCompanies.length > 0) {
      result = result.filter(r => selectedCompanies.includes(r.company || r.client_name || r.supplier_name || ''));
    }
    if (selectedStatuses.length > 0) {
      result = result.filter(r => selectedStatuses.includes(rowStatus(r)));
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selectedCompanies, selectedStatuses, type, invoiceType]);

  // Group by month on the row's own bucket (_mk, see lib/brosListRows).
  // Rows flagged _excluded (cancelled bookings without a fee, invoices that
  // bill no revenue booking) stay visible in `items` but out of the totals.
  const monthlyGroups = useMemo(() => {
    const groups = {};
    for (const r of filtered) {
      const mk = r._mk;
      if (!mk) continue;
      if (!groups[mk]) groups[mk] = [];
      groups[mk].push(r);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([mk, items]) => {
        const [, m] = mk.split('-');
        const monthIdx = parseInt(m, 10) - 1;
        const label = `${MONTH_NAMES[monthIdx]} ${mk.slice(0, 4)}`;
        const counted = items.filter(r => !r._excluded);
        if (type === 'bookings') {
          return {
            key: mk, label, items,
            count: counted.length,
            excludedCount: items.length - counted.length,
            totalClient: counted.reduce((s, b) => s + cv(b._client, b._cur), 0),
            totalSupplier: counted.reduce((s, b) => s + cv(b._supplier, b._cur), 0),
            totalMargin: counted.reduce((s, b) => s + cv(b._margin, b._cur), 0),
            totalNights: counted.reduce((s, b) => s + (b._nights || 0), 0),
          };
        }
        return {
          key: mk, label, items,
          count: counted.length,
          excludedCount: items.length - counted.length,
          totalAmount: counted.reduce((s, inv) => s + cv(inv._amount, inv._cur), 0),
          totalPaid: counted.reduce((s, inv) => s + cv(inv._paid, inv._cur), 0),
          totalOutstanding: counted.reduce((s, inv) => s + cv(inv._outstanding, inv._cur), 0),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, type, dc, fxRates]);

  const toggleMonth = (mk) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      next.has(mk) ? next.delete(mk) : next.add(mk);
      return next;
    });
  };

  const toggleMulti = (arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  // Grand totals
  const grandTotals = useMemo(() => {
    const counted = filtered.filter(r => !r._excluded);
    if (type === 'bookings') {
      return {
        count: counted.length,
        excludedCount: filtered.length - counted.length,
        nights: counted.reduce((s, b) => s + (b._nights || 0), 0),
        client: counted.reduce((s, b) => s + cv(b._client, b._cur), 0),
        supplier: counted.reduce((s, b) => s + cv(b._supplier, b._cur), 0),
        margin: counted.reduce((s, b) => s + cv(b._margin, b._cur), 0),
      };
    }
    return {
      count: counted.length,
      excludedCount: filtered.length - counted.length,
      amount: counted.reduce((s, inv) => s + cv(inv._amount, inv._cur), 0),
      paid: counted.reduce((s, inv) => s + cv(inv._paid, inv._cur), 0),
      outstanding: counted.reduce((s, inv) => s + cv(inv._outstanding, inv._cur), 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, type, dc, fxRates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No {type} found.
        </CardContent>
      </Card>
    );
  }

  const hasFilters = selectedCompanies.length > 0 || selectedStatuses.length > 0;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" /> Filters:
            </div>

            {type === 'invoices' && (
              <div className="inline-flex rounded border overflow-hidden text-sm">
                {[['client', 'Client invoices'], ['supplier', 'Supplier invoices']].map(([val, lbl]) => (
                  <button
                    key={val}
                    type="button"
                    className={cn('px-2 py-1', invoiceType === val ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted')}
                    onClick={() => { setInvoiceType(val); setSelectedCompanies([]); }}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            )}

            <select
              className="text-sm border rounded px-2 py-1 bg-card"
              value=""
              onChange={e => { if (e.target.value) toggleMulti(selectedCompanies, setSelectedCompanies, e.target.value); }}
            >
              <option value="">{type === 'invoices' && invoiceType === 'supplier' ? 'Supplier' : 'Company'} ({selectedCompanies.length || 'All'})</option>
              {companyOptions.map(c => (
                <option key={c} value={c}>{selectedCompanies.includes(c) ? '\u2713 ' : ''}{c}</option>
              ))}
            </select>

            <select
              className="text-sm border rounded px-2 py-1 bg-card"
              value=""
              onChange={e => { if (e.target.value) toggleMulti(selectedStatuses, setSelectedStatuses, e.target.value); }}
            >
              <option value="">Status ({selectedStatuses.length || 'All'})</option>
              {statusOptions.map(s => (
                <option key={s} value={s}>{selectedStatuses.includes(s) ? '\u2713 ' : ''}{s}</option>
              ))}
            </select>

            {hasFilters && (
              <>
                {selectedCompanies.map(c => (
                  <Badge key={c} variant="secondary" className="text-xs cursor-pointer" onClick={() => toggleMulti(selectedCompanies, setSelectedCompanies, c)}>
                    {c} x
                  </Badge>
                ))}
                {selectedStatuses.map(s => (
                  <Badge key={s} variant="secondary" className="text-xs cursor-pointer" onClick={() => toggleMulti(selectedStatuses, setSelectedStatuses, s)}>
                    {s} x
                  </Badge>
                ))}
                <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => { setSelectedCompanies([]); setSelectedStatuses([]); }}>
                  Clear all
                </Button>
              </>
            )}

            <span className="text-xs text-muted-foreground ml-auto">
              {filtered.length} {type === 'bookings' ? 'booking' : 'invoice'}{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 px-1">
            {type === 'bookings'
              ? (dueBasis
                ? 'Grouped by invoice due date, the same basis as the P&L chart: each booking shows the part of its value invoiced with a due date in that month (ex VAT, GBP converted), so month totals equal the chart. A cancelled booking with a cancellation fee shows as Cancellation. Bookings by closing date are under Sales > Bookings.'
                : 'Grouped by closing date (creation date when there is no close date), whole booking value. A cancelled booking with a cancellation fee shows as Cancellation, valued at the fee (ex VAT) and counted in the totals.')
              : `Grouped by due date, ex VAT, the same basis as the P&L and Cashflow charts. ${invoiceType === 'supplier' ? 'Supplier invoices (money out).' : 'Client invoices (money in). An invoice not linked to a booking is listed as "Not in revenue" and left out of the totals.'}`}
          </p>
        </CardContent>
      </Card>

      {/* Monthly expandable table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Month</TableHead>
                  {type === 'bookings' ? (
                    <>
                      <TableHead className="text-right">{dueBasis ? 'Revenue (due this month)' : 'Booking value'}</TableHead>
                      <TableHead className="text-right">Supplier Cost</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="text-right">Amount ex VAT</TableHead>
                      {showPaymentStatus && <TableHead className="text-right">Paid</TableHead>}
                      {showPaymentStatus && <TableHead className="text-right">Outstanding</TableHead>}
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyGroups.map(g => {
                  const isExpanded = expandedMonths.has(g.key);
                  return (
                    <React.Fragment key={g.key}>
                      {/* Month header row */}
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleMonth(g.key)}
                      >
                        <TableCell className="w-8 px-2">
                          {isExpanded
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </TableCell>
                        <TableCell className="font-medium">
                          {g.label}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {g.count} {type === 'bookings' ? 'booking' : 'invoice'}{g.count !== 1 ? 's' : ''}
                            {type === 'bookings' && g.totalNights > 0 && `, ${g.totalNights} night${g.totalNights !== 1 ? 's' : ''}`}
                            {type === 'bookings' && g.excludedCount > 0 && ` (excl. ${g.excludedCount} cancelled)`}
                            {type === 'invoices' && g.excludedCount > 0 && ` (excl. ${g.excludedCount} not in revenue)`}
                          </span>
                        </TableCell>
                        {type === 'bookings' ? (
                          <>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(g.totalClient, dc)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-red-500">
                              {formatCurrency(g.totalSupplier, dc)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(g.totalMargin, dc)}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(g.totalAmount, dc)}
                            </TableCell>
                            {showPaymentStatus && (
                              <TableCell className="text-right font-mono text-green-600">
                                {formatCurrency(g.totalPaid, dc)}
                              </TableCell>
                            )}
                            {showPaymentStatus && (
                              <TableCell className="text-right font-mono text-amber-600">
                                {formatCurrency(g.totalOutstanding, dc)}
                              </TableCell>
                            )}
                          </>
                        )}
                      </TableRow>

                      {/* Expanded detail rows */}
                      {isExpanded && g.items.map((r, i) => (
                        type === 'bookings' ? (
                          <TableRow key={`${g.key}-${i}`} className="bg-muted/20 text-xs">
                            <TableCell></TableCell>
                            <TableCell className="py-1.5">
                              <span className="font-medium">{r.company || r.client_name || '(unknown)'}</span>
                              <span className="text-muted-foreground ml-2">{r.property_name}</span>
                              <span className="text-muted-foreground ml-2">{r.check_in} to {r.check_out}</span>
                              <span className="text-muted-foreground ml-1">({r.num_nights}n)</span>
                              {r._share != null && r._share < 0.9995 && (
                                <span className="text-muted-foreground ml-2" title="Share of this booking's value invoiced with a due date in this month">
                                  {Math.round(r._share * 100)}% of booking
                                </span>
                              )}
                              <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">{r.currency}</Badge>
                              {r.status === 'cancellation' ? (
                                <Badge className="ml-1 text-[10px] px-1 py-0 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0" title="Cancellation fee: client invoices on this cancelled booking, ex VAT">Cancellation</Badge>
                              ) : r.status && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0">{r.status}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono py-1.5">
                              {dueBasis ? formatCurrency(cv(r._client, r._cur), dc) : formatCurrency(r._client, r._cur)}
                            </TableCell>
                            <TableCell className="text-right font-mono py-1.5 text-red-500">
                              {dueBasis ? formatCurrency(cv(r._supplier, r._cur), dc) : formatCurrency(r._supplier, r._cur)}
                              {!dueBasis && r.supplier_currency && r.supplier_currency !== (r.currency || 'GBP') && (
                                <div className="text-[10px] text-muted-foreground">
                                  {formatCurrency(r.supplier_total || 0, r.supplier_currency)} paid to supplier
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono py-1.5">
                              {dueBasis ? formatCurrency(cv(r._margin, r._cur), dc) : formatCurrency(r._margin, r._cur)}
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow key={`${g.key}-${i}`} className="bg-muted/20 text-xs">
                            <TableCell></TableCell>
                            <TableCell className="py-1.5">
                              <span className="font-medium">{invoiceParty(r)}</span>
                              {r.bros_id && (
                                <span className="text-muted-foreground ml-2">#{r.bros_id}</span>
                              )}
                              <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">{r.currency}</Badge>
                              {r.type && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0">{r.type}</Badge>
                              )}
                              <PaymentStatusBadge status={rowStatus(r)} />
                              {r._excluded && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0" title="Not linked to a revenue booking, so the P&L does not count it">Not in revenue</Badge>
                              )}
                              <InvoicePdfButton invoice={r} variant="icon" className="ml-2" />
                              {r.due_date && (
                                <span className="text-muted-foreground ml-2">due {r.due_date}</span>
                              )}
                              {r.issue_date && (
                                <span className="text-muted-foreground ml-2">issued {r.issue_date}</span>
                              )}
                            </TableCell>
                            <TableCell className={cn('text-right font-mono py-1.5', r._excluded && 'text-muted-foreground line-through')}>
                              {formatCurrency(r._amount, r._cur)}
                              {r._gross !== r._amount && (
                                <div className="text-[10px] text-muted-foreground no-underline">{formatCurrency(r._gross, r._cur)} incl VAT</div>
                              )}
                            </TableCell>
                            {showPaymentStatus && (
                              <TableCell className="text-right font-mono py-1.5 text-green-600">
                                {formatCurrency(r._paid, r._cur)}
                              </TableCell>
                            )}
                            {showPaymentStatus && (
                              <TableCell className="text-right font-mono py-1.5 text-amber-600">
                                {formatCurrency(r._outstanding, r._cur)}
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* Total row */}
                <TableRow className="border-t-2 font-semibold bg-muted/30">
                  <TableCell></TableCell>
                  <TableCell>
                    {type === 'bookings'
                      ? `Total (${grandTotals.count} bookings, ${grandTotals.nights} nights${grandTotals.excludedCount > 0 ? `, excl. ${grandTotals.excludedCount} cancelled` : ''})`
                      : `Total (${grandTotals.count} invoices${grandTotals.excludedCount > 0 ? `, excl. ${grandTotals.excludedCount} not in revenue` : ''})`}
                  </TableCell>
                  {type === 'bookings' ? (
                    <>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(grandTotals.client, dc)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-500">
                        {formatCurrency(grandTotals.supplier, dc)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(grandTotals.margin, dc)}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(grandTotals.amount, dc)}
                      </TableCell>
                      {showPaymentStatus && (
                        <TableCell className="text-right font-mono text-green-600">
                          {formatCurrency(grandTotals.paid, dc)}
                        </TableCell>
                      )}
                      {showPaymentStatus && (
                        <TableCell className="text-right font-mono text-amber-600">
                          {formatCurrency(grandTotals.outstanding, dc)}
                        </TableCell>
                      )}
                    </>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
